import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiWrapper';
import { useLanguage } from '../i18n/LanguageContext';
import './DeviceList.css';

function DeviceList() {
  const { t } = useLanguage();
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    status: '',
    type: '',
    search: ''
  });

  const [showModal, setShowModal] = useState(false);
  const [editDevice, setEditDevice] = useState(null);
  const [formData, setFormData] = useState({
    device_name: '',
    device_type: 'smoke_detector',
    location: '',
    status: 'online'
  });

  useEffect(() => {
    loadDevices();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const applyFilters = useCallback(() => {
    if (!Array.isArray(devices)) {
      setFilteredDevices([]);
      return;
    }
    let filtered = [...devices];
    if (filter.status) {
      filtered = filtered.filter(d => d && d.status === filter.status);
    }
    if (filter.type) {
      filtered = filtered.filter(d => d && d.device_type === filter.type);
    }
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      filtered = filtered.filter(d =>
        d && (
          (d.device_name && d.device_name.toLowerCase().includes(searchLower)) ||
          (d.location && d.location.toLowerCase().includes(searchLower)) ||
          (d.qr_code && d.qr_code.toLowerCase().includes(searchLower))
        )
      );
    }
    setFilteredDevices(filtered);
  }, [filter, devices]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const response = await apiService.devices.getAll();
      const data = response?.data;
      if (Array.isArray(data)) {
        setDevices(data);
      } else if (data && Array.isArray(data.devices)) {
        setDevices(data.devices);
      } else if (data && Array.isArray(data.data)) {
        setDevices(data.data);
      } else if (data && typeof data === 'string' && (data.trim().toLowerCase().startsWith('<!') || data.includes('<!doctype'))) {
        console.warn('API върна HTML вместо JSON – backend не е достъпен или REACT_APP_API_URL е грешен. Стартирайте backend (node server.js) и/или задайте правилен REACT_APP_API_URL при build.');
        setDevices([]);
      } else {
        const isHtml = typeof data === 'string' && (data.trim().toLowerCase().startsWith('<!') || data.includes('<!doctype'));
        if (isHtml) {
          console.warn('API върна HTML вместо JSON – проверете дали backend сървърът работи и REACT_APP_API_URL.');
        } else {
          console.warn('Неочакван формат на данните:', typeof data, Array.isArray(data) ? 'масив' : '');
        }
        setDevices([]);
      }
    } catch (error) {
      console.error('Грешка при зареждане на устройства:', error?.message || error);
      setDevices([]);
      const msg = error?.message || t('devices.errorLoading');
      if (msg.includes('HTML') || msg.includes('REACT_APP_API')) {
        alert(msg + '\n\nMake sure the backend server is running (e.g. node server.js in the backend folder) and that REACT_APP_API_URL=http://localhost:5000 or the correct API URL is set at build time.');
      } else {
        alert(t('devices.errorLoading'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilter({ status: '', type: '', search: '' });
  };

  const openCreateModal = () => {
    setEditDevice(null);
    setFormData({
      device_name: '',
      device_type: 'smoke_detector',
      location: '',
      status: 'online'
    });
    setShowModal(true);
  };

  const openEditModal = (device) => {
    setEditDevice(device);
    setFormData({
      device_name: device.device_name,
      device_type: device.device_type,
      location: device.location,
      status: device.status
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditDevice(null);
  };

  const handleFormChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editDevice) {
        await apiService.devices.update(editDevice.id, formData);
        alert(t('devices.successUpdate'));
      } else {
        await apiService.devices.create(formData);
        alert(t('devices.successCreate'));
      }

      closeModal();
      loadDevices();
    } catch (error) {
      console.error('Грешка при запис:', error);
      alert(t('devices.errorSaving'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('devices.confirmDelete'))) {
      return;
    }

    try {
      await apiService.devices.delete(id);
      alert(t('devices.successDelete'));
      loadDevices();
    } catch (error) {
      console.error('Грешка при изтриване:', error);
      alert(t('devices.errorDeleting'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      online: { class: 'badge-online', icon: '✅', text: t('devices.online') },
      offline: { class: 'badge-offline', icon: '⚫', text: t('devices.offline') },
      alarm: { class: 'badge-alarm', icon: '🚨', text: t('devices.alarm') }
    };
    const badge = badges[status] || badges.offline;
    return (
      <span className={`badge ${badge.class}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const types = {
      smoke_detector: `💨 ${t('devices.smokeDetector')}`,
      heat_sensor: `🌡️ ${t('devices.heatSensor')}`,
      camera: `📷 ${t('devices.camera')}`,
      sprinkler: `💧 ${t('devices.sprinkler')}`,
      alarm: `🔔 ${t('devices.alarmSystem')}`
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="devices-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('devices.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="devices-container">
      <div className="devices-header">
        <h2>📱 {t('devices.title')}</h2>
        <button className="btn btn-primary" onClick={openCreateModal}>
          ➕ {t('devices.addDevice')}
        </button>
      </div>

      {/* Филтри */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>🔍 Search:</label>
          <input
            type="text"
            placeholder="Name, location, QR code..."
            value={filter.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>📊 Status:</label>
          <select
            value={filter.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('common.all')}</option>
            <option value="online">{t('devices.online')}</option>
            <option value="offline">{t('devices.offline')}</option>
            <option value="alarm">{t('devices.alarm')}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>🔧 Type:</label>
          <select
            value={filter.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="filter-select"
          >
            <option value="">{t('common.all')}</option>
            <option value="smoke_detector">{t('devices.smokeDetector')}</option>
            <option value="heat_sensor">{t('devices.heatSensor')}</option>
            <option value="camera">{t('devices.camera')}</option>
            <option value="sprinkler">{t('devices.sprinkler')}</option>
            <option value="alarm">{t('devices.alarmSystem')}</option>
          </select>
        </div>

        <button className="btn btn-secondary" onClick={clearFilters}>
          🔄 {t('common.clear')}
        </button>
      </div>

      {/* Таблица с устройства */}
      <div className="card">
        <p className="results-count">
          {t('common.showing')}: {filteredDevices.length} {t('common.of')} {devices.length} {t('devices.title').toLowerCase()}
        </p>

        {filteredDevices.length > 0 ? (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('common.name')}</th>
                  <th>{t('common.type')}</th>
                  <th>{t('devices.location')}</th>
                  <th>{t('devices.status')}</th>
                  <th>{t('common.qrCode')}</th>
                  <th>{t('common.lastMaintenance')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map(device => (
                  <tr key={device.id}>
                    <td>{device.id}</td>
                    <td><strong>{device.device_name}</strong></td>
                    <td>{getTypeLabel(device.device_type)}</td>
                    <td>📍 {device.location}</td>
                    <td>{getStatusBadge(device.status)}</td>
                    <td>
                      {device.qr_code ? (
                        <code className="qr-code">{device.qr_code}</code>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {device.last_maintenance ? (
                        new Date(device.last_maintenance).toLocaleDateString('bg-BG')
                      ) : (
                        <span className="text-muted">{t('common.noDataAvailable')}</span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => openEditModal(device)}
                        title={t('common.edit')}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(device.id)}
                        title={t('common.delete')}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-data">{t('common.noResults')}</p>
        )}
      </div>

      {/* Modal за създаване/редактиране */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editDevice ? `✏️ ${t('common.editDevice')}` : `➕ ${t('common.newDevice')}`}
              </h3>
              <button className="modal-close" onClick={closeModal}>✖</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('devices.deviceName')} *</label>
                <input
                  type="text"
                  name="device_name"
                  value={formData.device_name}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g. Detector Floor 2"
                />
              </div>

              <div className="form-group">
                <label>{t('devices.deviceType')} *</label>
                <select
                  name="device_type"
                  value={formData.device_type}
                  onChange={handleFormChange}
                  required
                >
                  <option value="smoke_detector">💨 {t('devices.smokeDetector')}</option>
                  <option value="heat_sensor">🌡️ {t('devices.heatSensor')}</option>
                  <option value="camera">📷 {t('devices.camera')}</option>
                  <option value="sprinkler">💧 {t('devices.sprinkler')}</option>
                  <option value="alarm">🔔 {t('devices.alarmSystem')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('devices.location')} *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  required
                  placeholder="e.g. City, Street 10, Floor 2"
                />
              </div>

              <div className="form-group">
                <label>{t('devices.status')} *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  required
                >
                  <option value="online">✅ {t('devices.online')}</option>
                  <option value="offline">⚫ {t('devices.offline')}</option>
                  <option value="alarm">🚨 {t('devices.alarm')}</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {editDevice ? t('common.saveChanges') : t('common.createDevice')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceList;
